import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'

import { Router } from 'react-router-dom'
import { withLDProvider } from 'launchdarkly-react-client-sdk'

import { createBrowserHistory } from 'history'
import { Loader } from '@/components/loaders/Loader'
import { Toasts } from '@/components/toasts/Toasts'
import { BaltoGPT } from '@/components/baltoGPT/BaltoGPT'

import store from './store'
import App from './App'
import { buildUserAttributes } from './utils/launchDarkly'
import ErrorBoundary from './components/layout/ErrorBoundary'
import RtcSocketProvider from './components/providers/RtcSocketProvider'
import NotificationProvider from './components/providers/NotificationProvider'
import CommandCenterSocketProvider from './components/providers/CommandCenterSocketProvider'
import OrganizationsProvider from './components/providers/OrganizationsProvider'
import CustomBrandingProvider from './components/providers/CustomBrandingProvider'
import UserPermissionsProvider from './components/providers/UserPermissionsProvider'
import { IdleMonitorProvider } from './components/providers/IdleMonitorProvider'
import { Prompt } from './components/prompts/Prompt'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import './assets/styles/palette.scss' // Raw color palette the tokens are built from
import './assets/styles/variables.scss' // All base variables that the rest of the CSS pulls from
import './assets/styles/theme.scss' // Global HTML/base styles
import './assets/styles/helpers.scss' // Helper classes
import { applyStoredTheme } from './utils/hooks/useTheme'

export const history = createBrowserHistory()

// Creating the portal here so we only make 1, and all the draggables can share it
if (document.body) {
  const portal = document.createElement('div')
  portal.classList.add('dnd-portal')
  document.body.appendChild(portal)
}
const currentAppVersion = process.env.COMMIT_HASH

console.info(`Version: ${currentAppVersion}`)

function renderApp() {
  const user = JSON.parse(localStorage.getItem('User'))
  const ldUser = user ? buildUserAttributes(user) : { key: 'Anonymous' }

  const launchDarklyConfig = {
    clientSideID: process.env.LAUNCH_DARKLY_CLIENTSIDE_ID,
    user: ldUser,
    options: {
      ...(process.env.LAUNCH_DARKLY_STREAMING_OPTION !== undefined && {
        streaming: process.env.LAUNCH_DARKLY_STREAMING_OPTION,
      }),
    },
  }

  const LDApp = withLDProvider(launchDarklyConfig)(() => (
    <Provider store={store}>
      <React.Suspense fallback={<Loader page />}>
        <ErrorBoundary>
          <RtcSocketProvider />
          <NotificationProvider />
          <OrganizationsProvider />
          <CustomBrandingProvider />
          <UserPermissionsProvider />
          <Router history={history}>
            {/* Inside the Router: it only holds the socket open where something
                consumes it, and Live Listen being on screen is one of those
                conditions, so it reads the current route. */}
            <CommandCenterSocketProvider />
            <IdleMonitorProvider />
            <Prompt />
            <Toasts />
            <BaltoGPT />
            <App />
          </Router>
        </ErrorBoundary>
      </React.Suspense>
    </Provider>
  ))

  applyStoredTheme()
  ReactDOM.render(<LDApp />, document.getElementById('content') || document.createElement('div'))
}

renderApp()
